import { Component, type ErrorInfo, type ReactNode } from 'react';
import {
  buildFailureExplanationV0,
  buildRetryActionBindingV0,
  FailureState,
} from '@shared/operational-shell-and-component-grammar';
import { logger } from '@shared/logger';
import { css } from '@shared/utils/omitUndefined';
import styles from './AppErrorBoundary.module.css';

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  error: Error | null;
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error('AppErrorBoundary caught render error', error, {
      componentStack: info.componentStack,
    });
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div
          className={css(styles.s0_minHeighdisplayalignItejustifyCpaddingbackgro)}
          data-testid="app-error-boundary"
          role="alert"
          aria-live="assertive"
        >
          <FailureState
            explanation={buildFailureExplanationV0({
              whatHappened: '페이지를 불러오지 못했습니다',
              whyItHappened:
                this.state.error.message || '렌더링 중 예기치 않은 오류가 발생했습니다.',
              whatChanged: '이 페이지의 UI가 표시되지 않았습니다.',
              whatDidNotChange: '서버 데이터·권한·세션은 이 오류로 변경되지 않았습니다.',
              whatCanHappenNext: '새로고침하거나 잠시 후 다시 시도해 주세요.',
              receiptOrSupportReference: 'app-error-boundary',
            })}
            actions={[buildRetryActionBindingV0('app.reload', '새로고침')]}
            onAction={(kind) => {
              if (kind === 'retry') this.handleReload();
            }}
          />
        </div>
      );
    }

    return this.props.children;
  }
}
